import { useEffect } from "react";

/**
 * Windows-style "Enter moves to the next field" behaviour for the whole app.
 *
 * The old desktop software let users press Enter to jump from one field to the
 * next in sequence. Browsers don't do that by default, so this hook installs a
 * single global keydown listener that replicates it:
 *
 *   - Enter in a data-entry field  -> move focus to the NEXT field on screen
 *   - Enter in the LAST field      -> nothing (we intentionally stop here; the
 *                                     user clicks Save manually)
 *   - Shift+Enter in a textarea    -> insert a new line (normal typing)
 *   - Enter in a textarea          -> move to the next field
 *   - Enter in an open dropdown /   -> left alone so the control can pick the
 *     date picker                     highlighted option/date first
 *
 * "Next field" follows natural on-screen order (the order the fields appear in
 * the page, top-to-bottom / left-to-right). Nothing needs to be added to each
 * form for this to work.
 *
 * Opt-out: put  data-enter-nav="off"  on any element (or a wrapper) that has
 * its own Enter behaviour (e.g. a search box or the pagination "go to page"
 * input) and this handler will leave it completely alone.
 */

// Elements we treat as "fields" the cursor can stop on.
const FIELD_SELECTOR = [
  "input:not([type=hidden]):not([type=button]):not([type=submit]):not([type=reset]):not([type=file])",
  "textarea",
  "select",
  "button.btn-dropdown", // the app's custom dropdown control
  '[contenteditable="true"]',
].join(",");

function isSkippable(el) {
  // Explicit opt-out anywhere up the tree.
  if (el.closest && el.closest('[data-enter-nav="off"]')) return true;
  // Read-only display fields are skipped, but never skip a real dropdown
  // (react-select renders a read-only input with role="combobox").
  const tag = el.tagName;
  if (
    (tag === "INPUT" || tag === "TEXTAREA") &&
    el.readOnly &&
    el.getAttribute("role") !== "combobox"
  ) {
    return true;
  }
  return false;
}

function isVisible(el) {
  if (!el || el.disabled) return false;
  if (el.getAttribute && el.getAttribute("aria-hidden") === "true") return false;
  if (el.getClientRects().length === 0) return false;
  const style = window.getComputedStyle(el);
  if (style.visibility === "hidden" || style.display === "none") return false;
  return true;
}

function handleEnter(e) {
  // Ignore Enter used to confirm IME/autocomplete composition.
  if (e.key !== "Enter" || e.isComposing || e.keyCode === 229) return;

  const el = e.target;
  if (!el || !el.matches || !el.matches(FIELD_SELECTOR)) return;
  if (isSkippable(el)) return;

  // Textarea: Enter advances, Shift+Enter inserts a new line.
  if (el.tagName === "TEXTAREA" && e.shiftKey) return;

  // An OPEN dropdown (react-select and friends set aria-expanded="true") should
  // get Enter first so it can select the highlighted option. The menu then
  // closes (aria-expanded="false") and the next Enter advances to the next
  // field. This is what makes "land on dropdown -> type -> Enter to pick ->
  // Enter to move on" work.
  if (el.getAttribute("aria-expanded") === "true") {
    return;
  }

  // An open date picker should get Enter first so it can pick the date.
  if (
    el.classList &&
    el.classList.contains("react-datepicker-ignore-onclickoutside")
  ) {
    return;
  }

  // Scope the search to the active modal if one is open, otherwise the main
  // page area. This keeps the cursor inside the form the user is working in.
  const root =
    (el.closest && el.closest('[aria-modal="true"]')) ||
    (el.closest && el.closest(".pagescreen")) ||
    document.body;

  const fields = Array.prototype.filter.call(
    root.querySelectorAll(FIELD_SELECTOR),
    (f) => isVisible(f) && !isSkippable(f)
  );

  const index = fields.indexOf(el);
  if (index === -1) return; // not part of the sequence; leave default behaviour

  // Stop the browser's default Enter (form submit / accidental button click).
  e.preventDefault();

  const next = fields[index + 1];
  if (!next) return; // last field -> stop here on purpose

  next.focus();
}

export function useEnterKeyNavigation() {
  useEffect(() => {
    // Capture phase so we run before component-level handlers.
    document.addEventListener("keydown", handleEnter, true);
    return () => document.removeEventListener("keydown", handleEnter, true);
  }, []);
}

export default useEnterKeyNavigation;
